/**
 * aiAssetSlotPaths
 *
 * Maps AI asset slot keys (hero, featured, option_N, carousel_N, stepper_N,
 * paywall_feature_N, section_N) to the
 * corresponding V2 StepNode content paths where V2ScreenView reads the
 * resolved image URL. Used to mirror `aiAssets.{slot}.experiments.{id}.generatedUrl`
 * onto the content tree so the live phone preview updates after image generation.
 *
 * Lives in screen-utils so both the frontend editor and the builder-service
 * strategy sync path (strategyFlowDocumentTransformer / aiAssetSyncDiff) can
 * consume it without pulling reactFlow UI code into the backend bundle.
 * The original `@modules/reactFlow/utils/aiAssetSlotPaths` path re-exports
 * from here for frontend compatibility.
 *
 * All content image fields on V2 use LocalizedImage:
 *   { translations: { [locale]: AssetReference } }
 *   AssetReference = { original?: string; small?: string }
 *
 * We write the generated URL into a per-locale AssetReference so
 * resolveAssetReference (contentResolver.ts) picks it up for the active locale.
 */

/**
 * Resolve the dot-notation path within a StepNode where the image URL for
 * a given slot should be written, scoped to the active locale.
 *
 * Returns null if the slot key is not supported — caller should no-op.
 */
export function getAiAssetSlotContentPath(
  slotKey: string,
  locale: string,
  stepKind?: string,
): string | null {
  if (slotKey === 'hero') {
    const normalizedKind = (stepKind ?? '').toLowerCase();
    if (normalizedKind.includes('paywall')) {
      return `content.featuredMedia.content.image.translations.${locale}`;
    }
    return `content.hero.translations.${locale}`;
  }
  if (slotKey === 'featured') {
    return `content.featuredMedia.content.image.translations.${locale}`;
  }
  const optionMatch = slotKey.match(/^option_(\d+)$/);
  if (optionMatch) {
    return `content.options.items.${optionMatch[1]}.image.translations.${locale}`;
  }
  const carouselMatch = slotKey.match(/^carousel_(\d+)$/);
  if (carouselMatch) {
    return `content.carousel.items.${carouselMatch[1]}.content.image.translations.${locale}`;
  }
  const stepperMatch = slotKey.match(/^stepper_(\d+)$/);
  if (stepperMatch) {
    return `content.stepper.items.${stepperMatch[1]}.content.image.translations.${locale}`;
  }
  const paywallFeatureMatch = slotKey.match(/^paywall_feature_(\d+)$/);
  if (paywallFeatureMatch) {
    return `content.paywall.features.${paywallFeatureMatch[1]}.image.translations.${locale}`;
  }
  const sectionMatch = slotKey.match(/^section_(\d+)$/);
  if (sectionMatch) {
    return `content.flexibleLayout.sections.${sectionMatch[1]}.mediaContent.content.image.translations.${locale}`;
  }
  return null;
}

/**
 * Build an AssetReference suitable for writing into a LocalizedImage slot.
 *
 * Gemini image generation currently returns a single URL (no multi-size),
 * so we populate both `original` and `small` with the same URL — this lets
 * the resolver's size-fallback chain succeed regardless of which size the
 * render site requests.
 */
export function buildGeneratedAssetReference(generatedUrl: string): {
  original: string;
  small: string;
} {
  return { original: generatedUrl, small: generatedUrl };
}

type AiAssetsRecord = Record<string, any>;

export interface AiAssetMirrorOp {
  slotKey: string;
  experimentId: string;
  generatedUrl: string;
  path: string;
  value: {
    original: string;
    small: string;
  };
}

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isObjectLike(value: unknown): value is Record<string, any> | any[] {
  return !!value && typeof value === 'object';
}

function isArrayIndexKey(key: string): boolean {
  return /^\d+$/.test(key);
}

function setValueByDotPath(target: Record<string, any>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current: any = target;

  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    const nextKey = keys[i + 1];
    if (!isObjectLike(current[key])) {
      current[key] = isArrayIndexKey(nextKey) ? [] : {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

function ensureFeaturedImageMediaTarget(step: Record<string, any>): void {
  if (!isRecord(step.content)) {
    step.content = {};
  }

  if (!isRecord(step.content.featuredMedia)) {
    step.content.featuredMedia = {
      kind: 'Image',
      content: { image: { translations: {} } },
      appearance: {},
    };
    return;
  }

  const media = step.content.featuredMedia;
  media.kind = 'Image';

  if (!isRecord(media.content)) {
    media.content = {};
  }

  if (!isRecord(media.content.image)) {
    media.content.image = { translations: {} };
  }

  if (!isRecord(media.content.image.translations)) {
    media.content.image.translations = {};
  }

  if (!isRecord(media.appearance)) {
    media.appearance = {};
  }
}

/**
 * Apply a mirror op into a mutable StepNode-like object.
 *
 * Some render targets require more than the final URL leaf. For example,
 * Paywall renders its hero visual through FeaturedMedia, and Media returns
 * null unless the parent object has `kind: 'Image'` and `content.image`.
 */
export function applyAiAssetMirrorOpToStep(
  step: Record<string, any>,
  op: AiAssetMirrorOp,
): void {
  if (op.path.startsWith('content.featuredMedia.content.image.translations.')) {
    ensureFeaturedImageMediaTarget(step);
  }

  setValueByDotPath(step, op.path, op.value);
}

export interface CollectAiAssetMirrorOpsOptions {
  slotKeys?: Iterable<string>;
  experimentIdsBySlot?: Record<string, string | undefined>;
  stepKind?: string;
}

function isTerminalGeneratedExperiment(exp: any): boolean {
  return exp?.status === 'completed' || exp?.status === 'error' || !!exp?.generatedUrl;
}

function mergeAiAssetExperimentsForEditor(
  previousExperiments: AiAssetsRecord,
  nextExperiments: AiAssetsRecord,
): AiAssetsRecord {
  const mergedExperiments: AiAssetsRecord = {};
  const experimentIds = new Set([
    ...Object.keys(previousExperiments ?? {}),
    ...Object.keys(nextExperiments ?? {}),
  ]);

  for (const experimentId of experimentIds) {
    const prevExp = previousExperiments?.[experimentId];
    const nextExp = nextExperiments?.[experimentId];

    if (!prevExp) {
      mergedExperiments[experimentId] = nextExp;
      continue;
    }
    if (!nextExp) {
      mergedExperiments[experimentId] = prevExp;
      continue;
    }

    if (prevExp.status === 'generating' && !isTerminalGeneratedExperiment(nextExp)) {
      mergedExperiments[experimentId] = {
        ...nextExp,
        ...prevExp,
      };
      continue;
    }

    mergedExperiments[experimentId] = {
      ...prevExp,
      ...nextExp,
    };
  }

  return mergedExperiments;
}

/**
 * Smart-merge local editor state with Firestore aiAssets updates.
 *
 * Firestore generation completion writes only experiment fields. The local
 * editor may still be the only source of selectedExperimentId or a duplicated
 * experiment shell, so replacing aiAssets wholesale would lose user context.
 */
export function mergeAiAssetsForEditor(
  previousAiAssets: AiAssetsRecord,
  nextAiAssets: AiAssetsRecord,
): AiAssetsRecord {
  const mergedAiAssets: AiAssetsRecord = {};
  const allSlotKeys = new Set([
    ...Object.keys(previousAiAssets ?? {}),
    ...Object.keys(nextAiAssets ?? {}),
  ]);

  for (const slotKey of allSlotKeys) {
    const prevSlot = previousAiAssets?.[slotKey];
    const nextSlot = nextAiAssets?.[slotKey];
    if (!prevSlot) {
      mergedAiAssets[slotKey] = nextSlot;
      continue;
    }
    if (!nextSlot) {
      mergedAiAssets[slotKey] = prevSlot;
      continue;
    }
    mergedAiAssets[slotKey] = {
      ...nextSlot,
      selectedExperimentId: prevSlot.selectedExperimentId ?? nextSlot.selectedExperimentId,
      experiments: mergeAiAssetExperimentsForEditor(
        prevSlot.experiments ?? {},
        nextSlot.experiments ?? {},
      ),
    };
  }

  return mergedAiAssets;
}

/**
 * Collect completed generated asset URLs that should be mirrored into
 * StepNode.content, where V2ScreenView and WebShell read image URLs from.
 */
export function collectAiAssetMirrorOps(
  aiAssets: AiAssetsRecord | undefined,
  locale: string,
  options: CollectAiAssetMirrorOpsOptions = {},
): AiAssetMirrorOp[] {
  if (!aiAssets || typeof aiAssets !== 'object') return [];

  const slotKeys = options.slotKeys
    ? Array.from(options.slotKeys)
    : Object.keys(aiAssets);
  const mirrorOps: AiAssetMirrorOp[] = [];

  for (const slotKey of slotKeys) {
    const slot = aiAssets[slotKey];
    if (!slot || typeof slot !== 'object') continue;

    const experiments = slot.experiments ?? {};
    const selectedId = options.experimentIdsBySlot?.[slotKey]
      ?? slot.selectedExperimentId
      ?? Object.keys(experiments)[0];
    if (!selectedId) continue;

    const exp = experiments[selectedId];
    if (exp?.status !== 'completed' || !exp?.generatedUrl) continue;

    const contentPath = getAiAssetSlotContentPath(slotKey, locale, options.stepKind);
    if (!contentPath) continue;

    mirrorOps.push({
      slotKey,
      experimentId: selectedId,
      generatedUrl: exp.generatedUrl as string,
      path: contentPath,
      value: buildGeneratedAssetReference(exp.generatedUrl as string),
    });
  }

  return mirrorOps;
}
